<template>
    <div>
        <div v-if="signInFormVisible" style="min-width: 400px; width: 40vw; margin: auto; margin-top: 100px;" class="border-box">
            <div v-show="isInitial || isInvoking || isInvalid">
                <h3 class="fg-color-blue">Sign in to TransparentChoice</h3>
                <br />

                <div class="form-group">
                    <label>Email:</label>
                    <input v-model="inputEmail" ref="inputEmailRef" type="email" class="form-control" placeholder="Email address" @keyup.enter="handleSignInCommandV1" autofocus>
                    <ValidationError context="Email" :errors="validationErrors"></ValidationError>
                </div>

                <div class="form-group">
                    <label>Password:</label>
                    <input v-model="inputPassword" ref="inputPasswordRef" type="password" class="form-control" placeholder="Password" @keyup.enter="handleSignInCommandV1">

                    <div class="text-right">
                        <a href="/#/organizations/users/requestresetpassword" class="text-dark">Forgot password?</a>
                    </div>
                </div>

                <div>
                    <div class="error"><br /></div>
                </div>

                <button v-show="signinCommandEnabled" @click="handleSignInCommandV1" @keyup.enter="handleSignInCommandV1" class="btn btn-lg btn-primary btn-block">Sign in</button>
                <button v-show="isInvoking" class="btn btn-lg btn-primary btn-block" disabled>Signing in...</button>
                <br />
                <!--<span>
                <span>Don't have an account?</span>
                <a href="/#/organizations/users/requestaccount">Request access</a>
            </span>-->
                <hr /><br />
                <a href="/#/organizations/users/sso" class="btn btn-secondary btn-lg btn-block">Sign in with SSO</a>
            </div>

            <div v-show="unableToAuthenticate">
                <h3 class="fg-color-blue">Unable to sign-in</h3>
                <br />

                <p>There can be many reasons for this, eg.:</p>
                <ul>
                    <li>email and password mismatch</li>
                    <li>typo or caps lock is enabled</li>
                    <li>account does not exist</li>
                    <li>no roles assigned</li>
                    <li>account is locked (eg. due to inactivity)</li>
                    <li>your access request is pending or was rejected</li>
                </ul>
                <p>
                    Please check your email for any alerts. You can also request reset your password
                    <a href="/#/organizations/users/requestresetpassword">here</a>.
                </p>
                <br />

                <button @click="handleTryAgain" class="btn btn-lg btn-primary btn-block">Try again</button>
            </div>
        </div>

        <div v-if="signedInAlertVisible" style="min-width: 400px; width: 40vw; margin: auto; margin-top: 100px;" class="border-box">
            <h3 class="fg-color-blue">You are already signed in as {{email}}</h3>
            <br />
            <button @click="inputSignedInAsDifferentUser=true" class="btn btn-lg btn-primary btn-block">Sign in as a different user</button>
            <a href="/" class="btn btn-lg btn-secondary btn-block">Go to my account</a>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted, watch } from 'vue'
    import { useRoute, useRouter } from 'vue-router'
    import ValidationError from '@common/Components/ValidationError.vue'
    import { useCurrentUserQueryV1 } from '@Organizations/Users/queries-vue-composables'
    import { useSignInCommandV1 } from '@Organizations/Users/commands-vue-composables'

    const route = useRoute()
    const router = useRouter()

    const returnUrl = computed<string | undefined>(() =>
    {
        const v = route.query.returnUrl
        return Array.isArray(v) ? v[0] : v
    })


    const inputEmail = ref('')
    const inputPassword = ref('')
    const inputSignedInAsDifferentUser = ref(false)

    const inputEmailRef = ref<HTMLInputElement | null>(null)
    const inputPasswordRef = ref<HTMLInputElement | null>(null)

    const { runCurrentUserQueryV1, modelCurrentUserQueryV1: userSessionDetails } =
        useCurrentUserQueryV1(false, false, false)


    const email = computed(() => userSessionDetails.value?.Email)
    const isAuthenticated = computed(() => result.value?.IsAuthenticated || false)
    const accessUrl = computed(() => result.value?.AccessUrl || null)


    const {
        invokeSignInCommandV1,
        invokingSignInCommandV1,
        validationErrors,
        succeedSignInCommandV1,
        errorSignInCommandV1,
        result
    } = useSignInCommandV1(inputEmail, inputPassword, returnUrl)


    const isInitial = computed(() =>
        !invokingSignInCommandV1.value &&
        !succeedSignInCommandV1.value &&
        !errorSignInCommandV1.value)

    const isInvoking = computed(() => invokingSignInCommandV1.value === true)
    const isInvalid = computed(() => validationErrors.value?.length > 0)
    const isFailed = computed(() => errorSignInCommandV1.value != null)


    const signInFormVisible = computed(() =>
        userSessionDetails.value &&
        !isAuthenticated.value &&
        (!email.value || inputSignedInAsDifferentUser.value)
    )

    const signedInAlertVisible = computed(() =>
        email.value && !inputSignedInAsDifferentUser.value
    )

    const signinCommandEnabled = computed(() =>
        isInitial.value || isInvalid.value
    )


    const unableToAuthenticate = computed(() =>
        result.value && !result.value.IsAuthenticated
    )

    onMounted(() =>
    {
        document.title = 'Sign in to TransparentChoice'
        runCurrentUserQueryV1()
    })

    // ----------------------------------
    // handlers
    // ----------------------------------

    function handleSignInCommandV1()
    {
        if (!inputEmail.value)
        {
            inputEmailRef.value?.focus()
            return
        }

        if (!inputPassword.value)
        {
            inputPasswordRef.value?.focus()
            return
        }

        invokeSignInCommandV1()
    }

    function handleTryAgain()
    {
        window.location.reload()
    }


    // ----------------------------------
    // redirect (replaces watcher on getter)
    // ----------------------------------

    watch(isAuthenticated, (ok) =>
    {
        if (ok && accessUrl.value)
        {
            window.location.href = accessUrl.value
        }
    })
</script>
